import { useCallback } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useActionContext } from './actionContext';
import { getShareAction } from './shared/shareAction';

type Options<CH extends ShareChannel = ShareChannel> = {
	trackingEventName: string;
	config: ShareConfig;
	getShortUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

export function useGetShareAction<CH extends ShareChannel = ShareChannel>({
	getShortUrl,
	getTrackingData,
	getContent,
	trackingEventName,
	config,
	onSuccess,
	onFailure,
}: Options<CH>): (channel: CH) => () => Promise<boolean> {
	const actionContext = useActionContext();
	return useCallback(
		(channel) => async () => {
			const url = getShortUrl(channel);
			const success = await getShareAction(channel)(
				{
					url,
					trackingEventName,
					trackingData: getTrackingData(channel),
					content: {
						title: getContent(channel, 'title', { url }),
						body: getContent(channel, 'body', { url }),
						image: getContent(channel, 'image', { url }),
					},
					config,
				},
				actionContext,
			);
			if (success) {
				void onSuccess?.(channel);
			} else {
				void onFailure?.(channel);
			}
			return success;
		},
		[actionContext, getShortUrl, getTrackingData, getContent, onSuccess, onFailure, trackingEventName, config],
	);
}
